/**
 * Matrix4 and Vector Math Utilities
 */

import type { Vec3 } from "./types";

export const Mat4 = {
  identity: (): number[] => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],

  /**
   * Calculate the inverse of a 4x4 matrix
   * Using the adjugate method for general 4x4 matrices
   */
  inverse: (m: number[]): number[] => {
    const out = new Array(16).fill(0);
    
    // Calculate the determinant
    const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
    const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
    const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
    const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
    
    const b00 = a00 * a11 - a01 * a10;
    const b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10;
    const b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11;
    const b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30;
    const b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30;
    const b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31;
    const b11 = a22 * a33 - a23 * a32;
    
    // Calculate the determinant
    let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
    
    if (!det) {
      return Mat4.identity(); // Return identity matrix if not invertible
    }
    
    det = 1.0 / det;
    
    out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
    out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
    out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
    out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
    out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
    out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
    out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
    out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
    out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
    out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
    out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
    out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
    out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
    out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
    out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
    out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
    
    return out;
  },

  /**
   * Transpose a 4x4 matrix
   */
  transpose: (m: number[]): number[] => {
    return [
      m[0], m[4], m[8], m[12],
      m[1], m[5], m[9], m[13],
      m[2], m[6], m[10], m[14],
      m[3], m[7], m[11], m[15]
    ];
  },

  /**
   * Create a perspective projection matrix
   */
  perspective: (
    fov: number,
    aspect: number,
    near: number,
    far: number
  ): number[] => {
    const f = 1.0 / Math.tan(fov / 2);
    return [
      f / aspect,
      0,
      0,
      0,
      0,
      f,
      0,
      0,
      0,
      0,
      (far + near) / (near - far),
      -1,
      0,
      0,
      (2 * far * near) / (near - far),
      0,
    ];
  },

  /**
   * Create an orthographic projection matrix
   * This is ideal for pixel art as it preserves parallel lines and eliminates vanishing points
   */
  ortho: (
    left: number,
    right: number,
    bottom: number,
    top: number,
    near: number,
    far: number
  ): number[] => {
    const lr = 1 / (left - right);
    const bt = 1 / (bottom - top);
    const nf = 1 / (near - far);
    return [
      -2 * lr,
      0,
      0,
      0,
      0,
      -2 * bt,
      0,
      0,
      0,
      0,
      2 * nf,
      0,
      (left + right) * lr,
      (top + bottom) * bt,
      (far + near) * nf,
      1,
    ];
  },

  /**
   * Multiply two 4x4 matrices
   */
  multiply: (a: number[], b: number[]): number[] => {
    const out = new Array(16).fill(0);
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        for (let k = 0; k < 4; k++) {
          out[i * 4 + j] += a[i * 4 + k] * b[k * 4 + j];
        }
      }
    }
    return out;
  },

  /**
   * Create a rotation matrix around Y axis
   */
  rotateY: (m: number[], angle: number): number[] => {
    const c = Math.cos(angle),
      s = Math.sin(angle);
    const rot = [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1];
    return Mat4.multiply(m, rot);
  },

  /**
   * Create a rotation matrix around X axis
   */
  rotateX: (m: number[], angle: number): number[] => {
    const c = Math.cos(angle),
      s = Math.sin(angle);
    const rot = [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1];
    return Mat4.multiply(m, rot);
  },

  /**
   * Create a rotation matrix around Z axis
   */
  rotateZ: (m: number[], angle: number): number[] => {
    const c = Math.cos(angle),
      s = Math.sin(angle);
    const rot = [c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    return Mat4.multiply(m, rot);
  },

  /**
   * Create a translation matrix
   */
  translate: (m: number[], x: number, y: number, z: number): number[] => {
    const trans = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1];
    return Mat4.multiply(m, trans);
  },

  /**
   * Create a scale matrix
   */
  scale: (m: number[], x: number, y: number, z: number): number[] => {
    const scl = [x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1];
    return Mat4.multiply(m, scl);
  },

  /**
   * Transform a Vec3 by a matrix
   */
  transformVec3: (m: number[], v: Vec3): Vec3 => {
    const x = v[0],
      y = v[1],
      z = v[2];
    const w = m[3] * x + m[7] * y + m[11] * z + m[15] || 1.0;
    return [
      (m[0] * x + m[4] * y + m[8] * z + m[12]) / w,
      (m[1] * x + m[5] * y + m[9] * z + m[13]) / w,
      (m[2] * x + m[6] * y + m[10] * z + m[14]) / w,
    ];
  },
};

/**
 * Vector utility functions
 */
export const Vec = {
  /**
   * Normalize a Vec3
   */
  normalize: (v: Vec3): Vec3 => {
    const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    return len > 0 ? [v[0] / len, v[1] / len, v[2] / len] : v;
  },

  /**
   * Dot product of two Vec3s
   */
  dot: (a: Vec3, b: Vec3): number => {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  },

  /**
   * Cross product of two Vec3s
   */
  cross: (a: Vec3, b: Vec3): Vec3 => {
    return [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0],
    ];
  },
};
